import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useReportTemplates, ReportWidget } from '@/hooks/useReportTemplates';
import { FileText, Trash2, Eye, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PageLoading } from '@/components/ui/page-loading';

interface ReportTemplateListProps {
  onLoadTemplate: (widgets: ReportWidget[]) => void;
}

export const ReportTemplateList = ({ onLoadTemplate }: ReportTemplateListProps) => {
  const { templates, isLoading, deleteTemplate } = useReportTemplates();

  if (isLoading) return <PageLoading message="Carregando templates..." />;

  if (!templates || templates.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">Nenhum template salvo</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Crie seu primeiro relatório customizado na aba "Criar Relatório"
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {templates.map(template => {
        const enabledWidgets = (template.widgets || []).filter((w: ReportWidget) => w.enabled);
        return (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base">{template.nome}</CardTitle>
                  {template.descricao && (
                    <CardDescription className="mt-1">{template.descricao}</CardDescription>
                  )}
                </div>
                <Badge variant="secondary" className="text-xs">
                  {enabledWidgets.length} widgets
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {enabledWidgets.slice(0, 4).map((w: ReportWidget) => (
                  <Badge key={w.id} variant="outline" className="text-xs">
                    {w.label}
                  </Badge>
                ))}
                {enabledWidgets.length > 4 && (
                  <Badge variant="outline" className="text-xs">
                    +{enabledWidgets.length - 4}
                  </Badge>
                )}
              </div>
              <div className="flex items-center text-xs text-muted-foreground gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(template.created_at), "dd/MM/yyyy", { locale: ptBR })}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 gap-1.5 text-xs"
                  onClick={() => onLoadTemplate(enabledWidgets.sort((a: ReportWidget, b: ReportWidget) => a.order - b.order))}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Visualizar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => deleteTemplate.mutate(template.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
